import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  RiskTolerance,
  InvestorProfileRequest,
  RecommendationResponse,
} from "@/types/portfolio-builder.types";

export type WizardStep = 1 | 2 | 3 | 4;

interface PortfolioBuilderState {
  // Wizard navigation
  step: WizardStep;
  setStep: (step: WizardStep) => void;
  nextStep: () => void;
  prevStep: () => void;

  // Step 1: Experience
  experienceStocks: number;
  experienceOptions: number;
  experienceFutures: number;
  experienceCrypto: number;
  setExperience: (
    field:
      | "experienceStocks"
      | "experienceOptions"
      | "experienceFutures"
      | "experienceCrypto",
    value: number
  ) => void;

  // Step 2: Financial
  annualIncome: number;
  liquidNetWorth: number;
  sovrunAllocation: number;
  setFinancial: (
    field: "annualIncome" | "liquidNetWorth" | "sovrunAllocation",
    value: number
  ) => void;

  // Step 3: Goals
  riskTolerance: RiskTolerance;
  targetReturn: number;
  setRiskTolerance: (value: RiskTolerance) => void;
  setTargetReturn: (value: number) => void;

  // Result state (not persisted)
  recommendation: RecommendationResponse | null;
  setRecommendation: (result: RecommendationResponse | null) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;

  // SFWL-011: tenant's enabled+scoped catalog has fewer strategies than the
  // recommendation engine requires. Distinct from `error` — not a failure,
  // an expected admin-configuration state. Cleared on new runs and reset.
  insufficientCatalog: { available: number; minRequired: number } | null;
  setInsufficientCatalog: (
    value: { available: number; minRequired: number } | null
  ) => void;

  // Profile getter
  getProfile: () => InvestorProfileRequest | null;

  // Reset
  reset: () => void;
}

const INITIAL_STATE = {
  step: 1 as WizardStep,
  experienceStocks: 3,
  experienceOptions: 2,
  experienceFutures: 3,
  experienceCrypto: 2,
  annualIncome: 100_000,
  liquidNetWorth: 500_000,
  sovrunAllocation: 100_000,
  riskTolerance: "Moderate" as RiskTolerance,
  targetReturn: 0.2,
  recommendation: null,
  isLoading: false,
  error: null,
  insufficientCatalog: null,
};

export const usePortfolioBuilderStore = create<PortfolioBuilderState>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      setStep: (step) => set({ step }),
      nextStep: () => set((s) => ({ step: Math.min(4, s.step + 1) as WizardStep })),
      prevStep: () => set((s) => ({ step: Math.max(1, s.step - 1) as WizardStep })),

      setExperience: (field, value) => set({ [field]: value }),
      setFinancial: (field, value) => set({ [field]: value }),
      setRiskTolerance: (riskTolerance) => set({ riskTolerance }),
      setTargetReturn: (targetReturn) => set({ targetReturn }),

      setRecommendation: (recommendation) => set({ recommendation }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      setInsufficientCatalog: (insufficientCatalog) => set({ insufficientCatalog }),

      getProfile: () => {
        const s = get();
        if (s.experienceFutures < 2) return null;
        if (s.sovrunAllocation < 10_000) return null;
        return {
          experienceStocks: s.experienceStocks,
          experienceOptions: s.experienceOptions,
          experienceFutures: s.experienceFutures,
          experienceCrypto: s.experienceCrypto,
          annualIncome: s.annualIncome,
          liquidNetWorth: s.liquidNetWorth,
          sovrunAllocation: s.sovrunAllocation,
          riskTolerance: s.riskTolerance,
          targetReturn: s.targetReturn,
        };
      },

      reset: () => set(INITIAL_STATE),
    }),
    {
      name: "portfolio-builder-store",
      partialize: (state) => ({
        experienceStocks: state.experienceStocks,
        experienceOptions: state.experienceOptions,
        experienceFutures: state.experienceFutures,
        experienceCrypto: state.experienceCrypto,
        annualIncome: state.annualIncome,
        liquidNetWorth: state.liquidNetWorth,
        sovrunAllocation: state.sovrunAllocation,
        riskTolerance: state.riskTolerance,
        targetReturn: state.targetReturn,
      }),
    }
  )
);
